import {ReactElement, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";

import {getContrats} from "@/Api/contrat";
import IconPrevious from "@/assets/iconPrevious.svg?react";
import {Button} from "@/components/Button/Button";
import {ErrorBoundary} from "@/components/ErrorBoundary/ErrorBoundary";
import {LoadingOrError} from "@/components/LoadingOrError/LoadingOrError";
import {TableauDeBord} from "@/components/TableauDeBord/TableauDeBord";
import {AppContext} from "@/context/AppContext";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useIsConnectedTest} from "@/hooks/useIsConnectedTest/useIsConnectedTest";
import {useLoadingState} from "@/hooks/useLoadingState/useLoadingState";
import {AppContext as AppContextType} from "@/ts/appContext";
import {Contrat} from "@/ts/contrat";

const ContentTableauDeBord = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const AdherentInfoContent = styled.div`
  display: flex;
  gap: 20px;
  & button {
    align-self: flex-start;
  }
`;
const AdherentName = styled.h2`
  margin: 5px 0;
  color: ${({theme}) => theme.colors.darkPrimary};
  flex: 1;
`;

export function TableauDeBordAdherent(): ReactElement | null {
  // Vérifie si l'utilisateur est connecté et redirige vers la page de connexion si ce n'est pas le cas
  const isAuthorized = useIsConnectedTest();
  const loadingState = useLoadingState();
  const {API, userType} = useGetContext<AppContextType>(
    AppContext,
    "AppContext"
  );

  const [allContrats, setAllContrats] = useState<Contrat[] | null | undefined>(
    null
  );

  const {currentCampagne, currentContremarque} =
    useGetContext<UserParamContextType>(UserParamContext, "UserParamsContext");
  const [savedCampagneAndContremarque, setSavedCampagneAndContremarque] =
    useState<{campagne: string; contremarque: string}>({
      campagne: "",
      contremarque: "",
    });

  const navigate = useNavigate();

  // Récupère les contrats et si l'utilisateur est un collaborateur, on aussi récupère l'adhérent
  const fetchTableauDeBord = async (): Promise<void> => {
    const {contrats = undefined} = await loadingState.executeWithLoading<{
      contrats: Contrat[];
    }>(() =>
      getContrats(API, currentCampagne.value, currentContremarque.value)
    );
    setAllContrats(
      contrats?.filter(
        contrat => contrat?.statut === "actif" || contrat?.statut === "cloture"
      ) || []
    );
  };

  useEffect(() => {
    // on déclanche l'appel que la modification de la campagne ou de la contremarque à réellement changer les valeur.
    if (
      currentContremarque?.value &&
      currentCampagne?.value &&
      (savedCampagneAndContremarque.campagne !== currentCampagne.value ||
        savedCampagneAndContremarque.contremarque !== currentContremarque.value)
    ) {
      fetchTableauDeBord();
      setSavedCampagneAndContremarque({
        campagne: currentCampagne.value,
        contremarque: currentContremarque.value,
      });
    }
  }, [currentCampagne, currentContremarque]);

  // si l'utilisateur n'est pas connecté, on ne rend pas la page
  if (!isAuthorized) {
    return null;
  }

  return (
    <ContentTableauDeBord>
      <LoadingOrError
        loadingState={loadingState}
        errorImage="/path/to/error-image.png"
        buttonToRefresh={true}
        onRetry={async () => await fetchTableauDeBord()}
        data={{contrats: allContrats}}
        dataValidator={data => {
          // données du tableau de bord pour un collaborateur
          if (userType === "Collaborateur") {
            return data && data?.contrats;
          }
          // données du tableau de bord pour un adhérent
          return data && data?.contrats;
        }}>
        <ErrorBoundary
          fallbackText="Erreur lors de la récupération du tableau de bord"
          buttonToRefresh={true}>
          <div>
            {userType === "Collaborateur" && allContrats ? (
              <AdherentInfoContent>
                <Button
                  label="RETOUR"
                  icon={IconPrevious}
                  onClick={() => navigate(-1)}
                />
                <AdherentName>{`${allContrats[0]?.adherent?.nom} - ${allContrats[0]?.adherent?.contremarque}`}</AdherentName>
              </AdherentInfoContent>
            ) : null}
            {/* <HookFormContextProvider>
              <SearchTableauBord />
            </HookFormContextProvider> */}
            <h1 style={{textAlign: "center"}}>
              Mes contrats pour la campagne : {currentCampagne.libelle}
            </h1>
          </div>

          <TableauDeBord contrats={allContrats as Contrat[]} />
        </ErrorBoundary>
      </LoadingOrError>
    </ContentTableauDeBord>
  );
}
